// src/views/Config/ConfigPage.js
import { useEffect, useState } from "react";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import MainButton from "../../components/Elements/MainButton/MainButton";
import SecondButton from "../../components/Elements/SecondButton/SecondButton";
import { ConfigService } from "../../services/ConfigService";
import "./ConfigPage.css";

export default function ConfigPage() {
  const { token } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingService, setSavingService] = useState(false);
  const [savingSecret, setSavingSecret] = useState(false);

  const [company, setCompany] = useState({
    company_name: "",
    company_address_line1: "",
    company_address_line2: "",
    company_vat_detail: "",
    company_vat: 0,
    company_logo_name: "",
    company_color: "",
    company_slogan: "",
    company_invoice_emails: [],
    company_contact_emails: [],
  });

  const [serviceConfig, setServiceConfig] = useState({
    key: Date.now(),
    affiliate_one_off_mrg_default: 0,
    affiliate_pay_go_mrg_default: 0,
  });

  const [secret, setSecret] = useState({
    GOOGLE_CLIENT_ID: "",
    GOOGLE_CLIENT_SECRET: "",
    GOOGLE_REDIRECT_URI: "",
    SMTP_HOST: "",
    SMTP_PORT: "",
    SMTP_USERNAME: "",
    SMTP_PASSWORD: "",
    STRIPE_API_SECRET_KEY: "",
    STRIPE_API_PUBLIC_KEY: "",
    STRIPE_WEBHOOK_SECRET: "",
    PAYPAL_ENV: "production",
    PAYPAL_BASE_URL: "",
    PAYPAL_CLIENT_ID: "",
    PAYPAL_CLIENT_SECRET: "",
    PAYPAL_WEBHOOK_ID: "",
    BINANCE_API_KEY: "",
    BINANCE_SECRET_KEY: "",
    SERVICE_ACCOUNT_JSON: "",
    SPREADSHEET_ID_ESIM: "",
    WORKSHEET_NAME_SERVICE: "",
    WORKSHEET_NAME_ESIM: "",
  });

  useEffect(() => {
    const loadAll = async () => {
      try {
        const { company, service, secret } = await ConfigService.loadAllConfigs(token);
        setCompany({
          company_name: company?.company_name ?? "",
          company_address_line1: company?.company_address_line1 ?? "",
          company_address_line2: company?.company_address_line2 ?? "",
          company_vat_detail: company?.company_vat_detail ?? "",
          company_vat: company?.company_vat ?? 0,
          company_logo_name: company?.company_logo_name ?? "",
          company_color: company?.company_color ?? "",
          company_slogan: company?.company_slogan ?? "",
          company_invoice_emails: Array.isArray(company?.company_invoice_emails) ? company.company_invoice_emails : [],
          company_contact_emails: Array.isArray(company?.company_contact_emails) ? company.company_contact_emails : [],
        });
        setServiceConfig(service);
        setSecret(secret);
      } catch (e) {
        console.error(e);
        // TODO: toast
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadAll();
    }
  }, [token]);

  const handleCompanyChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "company_vat") {
      value = value === "" ? null : Number(value) || 0;
    }
    setCompany((prev) => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (field) => (e) => {
    const rawValue = e.target.value;
    const value = rawValue === "" ? null : Number(rawValue) || 0;
    setServiceConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecretChange = (field) => (e) => {
    setSecret((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await ConfigService.updateCompanyConfig(company, token);
      // toast success
    } catch (e) {
      console.error(e);
      // toast error
    } finally {
      setSavingCompany(false);
    }
  };

  const saveService = async () => {
    setSavingService(true);
    try {
      await ConfigService.updateServiceConfig(serviceConfig, token);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingService(false);
    }
  };

  const saveSecret = async () => {
    setSavingSecret(true);
    try {
      await ConfigService.updateSecretConfig(secret, token);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingSecret(false);
    }
  };

  if (loading) {
    return (
      <main className="partner-offers">
        <PageHeader title="Configuration" description="Loading configuration..." />
        <section className="partner-offers__wrap">
          <p className="partner-offers__placeholder">Loading…</p>
        </section>
      </main>
    );
  }

  return (
    <main className="partner-offers">
      <PageHeader title="Configuration" description="Manage company data, service defaults and secrets." />

      <section className="partner-offers__wrap config-wrap">
        {/* 1. Company Data */}
        <div className="config-panel">
          <div className="config-panel__head">
            <h3 className="config-panel__title">Company Data</h3>
            <p className="config-panel__note">Basic company information used in invoices and communication.</p>
          </div>

          <div className="config-grid">
            <div className="config-field">
              <label>Company name</label>
              <input type="text" value={company.company_name} onChange={handleCompanyChange("company_name")} />
            </div>

            <div className="config-field ">
              <label>Company slogan</label>
              <input type="text" value={company.company_slogan} onChange={handleCompanyChange("company_slogan")} />
            </div>

            <div className="config-field">
              <label>Address line 1</label>
              <input
                type="text"
                value={company.company_address_line1}
                onChange={handleCompanyChange("company_address_line1")}
              />
            </div>

            <div className="config-field">
              <label>Address line 2</label>
              <input
                type="text"
                value={company.company_address_line2}
                onChange={handleCompanyChange("company_address_line2")}
              />
            </div>

            <div className="config-field">
              <label>VAT detail</label>
              <input
                type="text"
                value={company.company_vat_detail}
                onChange={handleCompanyChange("company_vat_detail")}
              />
            </div>

            <div className="config-field">
              <label>VAT rate</label>
              <input
                type="text"
                step="0.01"
                value={company.company_vat ?? ""}
                onChange={handleCompanyChange("company_vat")}
              />
            </div>

            <div className="config-field">
              <label>Logo file name</label>
              <input
                type="text"
                value={company.company_logo_name}
                onChange={handleCompanyChange("company_logo_name")}
              />
            </div>

            <div className="config-field">
              <label>Brand color (hex)</label>
              <input type="text" value={company.company_color} onChange={handleCompanyChange("company_color")} />
            </div>

            <div className="config-field config-field--full">
              <label>Invoice emails (comma separated)</label>
              <input
                type="text"
                value={company.company_invoice_emails.join(", ")}
                onChange={(e) =>
                  setCompany((prev) => ({
                    ...prev,
                    company_invoice_emails: e.target.value
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean),
                  }))
                }
              />
            </div>

            <div className="config-field config-field--full">
              <label>Contact emails (comma separated)</label>
              <input
                type="text"
                value={company.company_contact_emails.join(", ")}
                onChange={(e) =>
                  setCompany((prev) => ({
                    ...prev,
                    company_contact_emails: e.target.value
                      .split(",")
                      .map((x) => x.trim())
                      .filter(Boolean),
                  }))
                }
              />
            </div>

            {/* <div className="config-field config-field--full">
              <label>VAT countries (ISO2, comma separated)</label>
              <input
                type="text"
                value={company.company_vat_countries_iso2.join(", ")}
                onChange={(e) =>
                  setCompany((prev) => ({
                    ...prev,
                    company_vat_countries_iso2: e.target.value
                      .split(",")
                      .map((x) => x.trim().toUpperCase())
                      .filter(Boolean),
                  }))
                }
              />
            </div> */}
          </div>

          <div className="config-panel__actions">
            <MainButton onClick={saveCompany} disabled={savingCompany}>
              {savingCompany ? "Saving…" : "Save Company"}
            </MainButton>
          </div>
        </div>

        {/* 2. Service Config */}
        <div className="config-panel">
          <div className="config-panel__head">
            <h3 className="config-panel__title">Service Config</h3>
            <p className="config-panel__note">Default margins applied to affiliate offers.</p>
          </div>

          <div className="config-grid">
            <div className="config-field">
              <label>Affiliate one-off MRG default</label>
              <input
                type="text"
                step="0.01"
                value={serviceConfig.affiliate_one_off_mrg_default ?? ""}
                onChange={handleServiceChange("affiliate_one_off_mrg_default")}
              />
            </div>

            <div className="config-field">
              <label>Affiliate pay-go MRG default</label>
              <input
                type="text"
                step="0.01"
                value={serviceConfig.affiliate_pay_go_mrg_default ?? ""}
                onChange={handleServiceChange("affiliate_pay_go_mrg_default")}
              />
            </div>
          </div>

          <div className="config-panel__actions">
            <MainButton onClick={saveService} disabled={savingService}>
              {savingService ? "Saving…" : "Save Service Config"}
            </MainButton>
          </div>
        </div>

        {/* 3. Secret Data */}
        <div className="config-panel">
          <div className="config-panel__head">
            <h3 className="config-panel__title">Secret Data</h3>
            <p className="config-panel__note">Integration keys and credentials. Changes apply immediately.</p>
          </div>

          <div className="config-grid config-grid--2cols">
            <div className="config-field">
              <label>Google Client ID</label>
              <input type="text" value={secret.GOOGLE_CLIENT_ID} onChange={handleSecretChange("GOOGLE_CLIENT_ID")} />
            </div>

            <div className="config-field">
              <label>Google Client Secret</label>
              <input
                type="text"
                value={secret.GOOGLE_CLIENT_SECRET}
                onChange={handleSecretChange("GOOGLE_CLIENT_SECRET")}
              />
            </div>

            <div className="config-field config-field--full">
              <label>Google Redirect URI</label>
              <input
                type="text"
                value={secret.GOOGLE_REDIRECT_URI}
                onChange={handleSecretChange("GOOGLE_REDIRECT_URI")}
              />
            </div>

            <div className="config-field">
              <label>SMTP Host</label>
              <input type="text" value={secret.SMTP_HOST} onChange={handleSecretChange("SMTP_HOST")} />
            </div>

            <div className="config-field">
              <label>SMTP Port</label>
              <input type="text" value={secret.SMTP_PORT} onChange={handleSecretChange("SMTP_PORT")} />
            </div>

            <div className="config-field">
              <label>SMTP Username</label>
              <input type="text" value={secret.SMTP_USERNAME} onChange={handleSecretChange("SMTP_USERNAME")} />
            </div>

            <div className="config-field">
              <label>SMTP Password</label>
              <input type="text" value={secret.SMTP_PASSWORD} onChange={handleSecretChange("SMTP_PASSWORD")} />
            </div>

            <div className="config-field">
              <label>Stripe API Public Key</label>
              <input
                type="text"
                value={secret.STRIPE_API_PUBLIC_KEY}
                onChange={handleSecretChange("STRIPE_API_PUBLIC_KEY")}
              />
            </div>

            <div className="config-field">
              <label>Stripe API Secret Key</label>
              <input
                type="text"
                value={secret.STRIPE_API_SECRET_KEY}
                onChange={handleSecretChange("STRIPE_API_SECRET_KEY")}
              />
            </div>

            <div className="config-field">
              <label>Stripe Webhook Secret</label>
              <input
                type="text"
                value={secret.STRIPE_WEBHOOK_SECRET}
                onChange={handleSecretChange("STRIPE_WEBHOOK_SECRET")}
              />
            </div>

            <div className="config-field">
              <label>PayPal Env</label>
              <input type="text" value={secret.PAYPAL_ENV} onChange={handleSecretChange("PAYPAL_ENV")} />
            </div>

            <div className="config-field">
              <label>PayPal Base URL</label>
              <input type="text" value={secret.PAYPAL_BASE_URL} onChange={handleSecretChange("PAYPAL_BASE_URL")} />
            </div>

            <div className="config-field">
              <label>PayPal Client ID</label>
              <input type="text" value={secret.PAYPAL_CLIENT_ID} onChange={handleSecretChange("PAYPAL_CLIENT_ID")} />
            </div>

            <div className="config-field">
              <label>PayPal Client Secret</label>
              <input
                type="text"
                value={secret.PAYPAL_CLIENT_SECRET}
                onChange={handleSecretChange("PAYPAL_CLIENT_SECRET")}
              />
            </div>

            <div className="config-field">
              <label>PayPal Webhook ID</label>
              <input type="text" value={secret.PAYPAL_WEBHOOK_ID} onChange={handleSecretChange("PAYPAL_WEBHOOK_ID")} />
            </div>

            <div className="config-field">
              <label>Binance API Key</label>
              <input type="text" value={secret.BINANCE_API_KEY} onChange={handleSecretChange("BINANCE_API_KEY")} />
            </div>

            <div className="config-field">
              <label>Binance Secret Key</label>
              <input
                type="text"
                value={secret.BINANCE_SECRET_KEY}
                onChange={handleSecretChange("BINANCE_SECRET_KEY")}
              />
            </div>

            <div className="config-field config-field--full">
              <label>Service account JSON</label>
              <textarea
                rows={4}
                value={secret.SERVICE_ACCOUNT_JSON}
                onChange={handleSecretChange("SERVICE_ACCOUNT_JSON")}
              />
            </div>

            <div className="config-field config-field--full">
              <label>Spreadsheet ID (ESIM)</label>
              <input
                type="text"
                value={secret.SPREADSHEET_ID_ESIM}
                onChange={handleSecretChange("SPREADSHEET_ID_ESIM")}
              />
            </div>

            <div className="config-field">
              <label>Worksheet name (Service)</label>
              <input
                type="text"
                value={secret.WORKSHEET_NAME_SERVICE}
                onChange={handleSecretChange("WORKSHEET_NAME_SERVICE")}
              />
            </div>

            <div className="config-field">
              <label>Worksheet name (ESIM)</label>
              <input
                type="text"
                value={secret.WORKSHEET_NAME_ESIM}
                onChange={handleSecretChange("WORKSHEET_NAME_ESIM")}
              />
            </div>
          </div>

          <div className="config-panel__actions">
            <MainButton onClick={saveSecret} disabled={savingSecret}>
              {savingSecret ? "Saving…" : "Save Secrets"}
            </MainButton>
          </div>
        </div>
      </section>
    </main>
  );
}
